import React from 'react';
import { Shield } from 'lucide-react';

/**
 * SafetyLevel - Shared component for displaying safety/security level
 * Used in both CoinInfo and Address pages
 *
 * @param {Object} props
 * @param {number|string|null} props.level - Security level (1-5), 'loading', null, or 0
 * @param {string|null} props.description - Short description of the security concern
 * @param {boolean} props.centered - Whether to center align content (default: false)
 */
const SafetyLevel = ({ level, description = null, centered = false }) => {
  const getSecurityInfo = (securityLevel) => {
    if (securityLevel === 'loading') {
      return { text: 'Loading...', color: 'text-gray-500', isLoading: true };
    }
    if (securityLevel == null || securityLevel === 0) {
      return { text: 'N/A', color: 'text-gray-600' };
    }
    switch (securityLevel) {
      case 1:
        return { text: '⚠️ Very Low', color: 'text-red-700' };
      case 2:
        return { text: '⚠️ Low', color: 'text-red-600' };
      case 3:
        return { text: '⚡ Medium', color: 'text-orange-600' };
      case 4:
        return { text: '✅ Good', color: 'text-yellow-700' };
      case 5:
        return { text: '✅ Excellent', color: 'text-green-700' };
      default:
        return { text: 'N/A', color: 'text-gray-600' };
    }
  };

  const getContainerClasses = (securityLevel) => {
    if (!securityLevel || securityLevel === 'loading') return 'bg-gray-50 border-gray-300';
    switch (securityLevel) {
      case 1:
      case 2:
        return 'bg-red-50 border-red-300';
      case 3:
        return 'bg-orange-50 border-orange-300';
      case 4:
        return 'bg-yellow-50 border-yellow-300';
      case 5:
        return 'bg-green-50 border-green-300';
      default:
        return 'bg-gray-50 border-gray-300';
    }
  };

  const securityInfo = getSecurityInfo(level);
  const containerClasses = getContainerClasses(level);

  return (
    <div className="mb-6 pb-6 border-b border-gray-200">
      <div className={`p-4 rounded-lg border-2 ${containerClasses}`}>
        <div className={`flex items-center gap-2 mb-2 ${centered ? 'justify-center' : ''}`}>
          <Shield className={`w-5 h-5 ${securityInfo.color}`} />
          <p className="text-sm font-semibold text-gray-700">Safety Level</p>
        </div>
        <div className={`text-2xl font-bold flex items-center gap-2 ${securityInfo.color} ${centered ? 'justify-center' : ''}`}>
          {securityInfo.isLoading && (
            <span className="inline-block w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
          )}
          {securityInfo.text}
        </div>
        {description && (
          <p className={`text-sm text-red-700 mt-1 ${centered ? 'text-center' : ''}`}>
            {description}
          </p>
        )}
        <p className={`text-xs text-gray-600 mt-2 ${centered ? 'text-center' : ''}`}>
          Based on fraud database lookup, contract verification, AI and community assessment etc. It could be wrong. Use it at your own discretion.
        </p>
      </div>
    </div>
  );
};

export default SafetyLevel;
